/* eslint-disable  @typescript-eslint/no-explicit-any */
import { request, Logger, uniqueId, RequestOptions } from '@nextgen-web-framework/core';
import { v5 as uuidv5 } from 'uuid';
import {
  GetCheckoutProps,
  createCheckoutProps,
  PlaceOrderProps,
  UpdatePaymentProps,
  CreatePaymentProps,
  GetCheckoutByOrderIdProps,
  CheckoutApiResponse,
  CheckoutProfileResponse,
  CheckoutIdReqPayloadType,
  FulfillmentIdReqPayloadType,
  confirmDeliveryOptionProps,
  updateDeliveryOptionProps,
  GetTaxDocumentsProps,
  CheckoutTaxResponse,
  updateCheckoutProps,
  ICheckoutAnyType,
  CustomRequestOptions,
  searchAmwayPeriodProps,
  updateAmwayPeriodProps,
  SearchAmwayPeriodApiResponse,
  AmwayPeriodApiResponse,
} from '../../types/checkoutTypes';
import {
  BaseAPIUrl,
  CheckoutId,
  FulfillmentId,
  PaymentId,
  getCheckoutDetailsReqObj,
  createCheckoutDetailsReqObj,
  placeOrderReqObj,
  createPaymentReqObj,
  updatePaymentReqObj,
  confirmDeliveryOptionReqObj,
  updateDeliveryOptionReqObj,
  getCheckoutProfileReqObj,
  getCheckoutByOrderIdReqObj,
  OrderId,
  getTaxDocumentsReqObj,
  updateCheckoutDetailsReqObj,
  getDaneCode,
  Profile_CITY,
  Profile_STATE,
  searchAmwayPeriodReqObj,
  updateAmwayPeriodReqObj,
  PeriodId,
} from '../config';
import { AmwayPeriodResponse, PaginatedResponse } from '../models';
import logObject from './../../../logObject';

const replaceURI = (uri: string) => uri.replace(/[#]/g, encodeURIComponent('#'));
const BadRequest = { code: 'UI-001', detail: 'Please check arguments' };
const baseApiUrl = ApiConfigService.getBaseUrl();
const logger = Logger.getInstance(logObject('checkoutApi.ts'));

export const isObjectEmpty = (obj?: unknown) => {
  let objEmpty = false;
  if (!obj || Object.keys(obj).length == 0) {
    objEmpty = true;
  }
  return objEmpty;
};

export const checkoutApi = {
  getCheckoutDetails(params: GetCheckoutProps): Promise<CheckoutApiResponse> {
    if (!params.checkoutId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...getCheckoutDetailsReqObj };
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    return handleCheckoutRequest(req);
  },
  createCheckout(params: createCheckoutProps): Promise<CheckoutApiResponse> {
    if (!params.payload) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...createCheckoutDetailsReqObj };
    req.body = params.payload as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },
  updateCheckout(params: updateCheckoutProps & CheckoutIdReqPayloadType): Promise<CheckoutApiResponse> {
    if (!params.payload || !params.checkoutId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...updateCheckoutDetailsReqObj };
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    req.body = params.payload as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },
  placeOrder(params: PlaceOrderProps): Promise<CheckoutApiResponse> {
    if (!params.payload.version || !params.payload.checkoutId) {
      return Promise.resolve(BadRequest);
    }

    const req = { ...placeOrderReqObj };
    req.url = req.url.replace(CheckoutId, params.payload.checkoutId);
    req.body = { version: params.payload.version } as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },

  createPaymentMethod(params: CreatePaymentProps & CheckoutIdReqPayloadType): Promise<CheckoutApiResponse> {
    if (!params.payload || !params.checkoutId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...createPaymentReqObj };
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    req.body = params.payload as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },

  updatePaymentMethod(params: UpdatePaymentProps & CheckoutIdReqPayloadType): Promise<CheckoutApiResponse> {
    if (!params.payload || !params.checkoutId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...updatePaymentReqObj };
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    req.url = req.url.replace(PaymentId, params.paymentId);
    req.body = params.payload as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },

  confirmDeliveryOption(params: confirmDeliveryOptionProps & CheckoutIdReqPayloadType): Promise<CheckoutApiResponse> {
    if (!params.payload || !params.checkoutId) {
      return Promise.resolve(BadRequest);
    }

    const req = { ...confirmDeliveryOptionReqObj };
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    req.body = params.payload as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },

  updateDeliveryOption(
    params: updateDeliveryOptionProps & CheckoutIdReqPayloadType & FulfillmentIdReqPayloadType,
  ): Promise<CheckoutApiResponse> {
    if (!params.payload || !params.checkoutId || !params.fulfillmentId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...updateDeliveryOptionReqObj };
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    req.url = req.url.replace(FulfillmentId, params.fulfillmentId);
    req.body = params.payload as unknown as BodyInit;
    return handleCheckoutRequest(req);
  },

  getCheckoutProfile(): Promise<CheckoutProfileResponse> {
    try {
      const req = { ...getCheckoutProfileReqObj };
      return handleCheckoutProfileRequest(req);
    } catch (err: any) {
      logger.error(err);
      return err;
    }
  },

  getDaneCode(params: ICheckoutAnyType): Promise<ICheckoutAnyType> {
    if (!params.city || !params.state) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...getDaneCode };
    req.url = req.url.replace(Profile_CITY, params.city);
    req.url = req.url.replace(Profile_STATE, params.state);
    return handleCheckoutProfileRequest(req);
  },

  getCheckoutByOrderId(params: GetCheckoutByOrderIdProps): Promise<CheckoutApiResponse> {
    if (!params.orderId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...getCheckoutByOrderIdReqObj };
    req.url = req.url.replace(OrderId, params.orderId);
    return handleCheckoutRequest(req);
  },
  getTaxDocuments(params: GetTaxDocumentsProps): Promise<CheckoutTaxResponse> {
    if (!params.checkoutId) {
      return Promise.resolve(BadRequest);
    }
    const req = { ...getTaxDocumentsReqObj };
    req.url = req.url.replace(BaseAPIUrl, baseApiUrl);
    req.url = req.url.replace(CheckoutId, params.checkoutId);
    return handleCheckoutTaxesRequest(req);
  },
  async searchAmwayPeriodRequest(data: searchAmwayPeriodProps): Promise<SearchAmwayPeriodApiResponse> {
    try {
      if (isObjectEmpty(data.payload) || isObjectEmpty(data.params)) {
        throw BadRequest;
      }
      const req = { ...searchAmwayPeriodReqObj };
      req.body = data.payload as unknown as BodyInit;
      req.params = data.params;
      return await handleGenericRequest<PaginatedResponse>(req);
    } catch (err) {
      logger.error(`Failed to search amway period request. Error: ${err}`);
      throw BadRequest;
    }
  },
  async updateAmwayPeriodRequest(data: updateAmwayPeriodProps): Promise<AmwayPeriodApiResponse> {
    try {
      const req = { ...updateAmwayPeriodReqObj };
      req.body = data.payload as unknown as BodyInit;
      req.url = req.url.replace(PeriodId, data.payload.id ?? '');
      return await handleGenericRequest<AmwayPeriodResponse>(req);
    } catch (err) {
      logger.error(`Failed to update amway period request. Error: ${err}`);
      throw err;
    }
  },
};

const handleCheckoutRequest = async (req: RequestOptions): Promise<CheckoutApiResponse> => {
  const response: CheckoutApiResponse = await request(
    { ...req, timeout: 20000 },
    {
      customKeyGenerator: () => uuidv5(req.url + uniqueId(), uuidv5.URL),
    },
  );
  return Promise.resolve(response);
};

const handleCheckoutProfileRequest = async (req: RequestOptions): Promise<CheckoutProfileResponse> => {
  const response: CheckoutProfileResponse = await request(
    { ...req, timeout: 20000 },
    {
      customKeyGenerator: () => uniqueId().toString(),
    },
  );
  return Promise.resolve(response);
};
const handleCheckoutTaxesRequest = async (req: RequestOptions): Promise<CheckoutTaxResponse> => {
  const response: CheckoutTaxResponse = await request(
    { ...req, timeout: 20000 },
    {
      customKeyGenerator: () => uniqueId().toString(),
    },
  );
  return Promise.resolve(response);
};

async function handleGenericRequest<T>(req: RequestOptions, options?: CustomRequestOptions): Promise<T> {
  req.url = replaceURI(req.url);
  const augments = {
    customKeyGenerator: () => uuidv5(req.url + uniqueId(), uuidv5.URL),
  };

  if (options?.headers) {
    req.headers = { ...req?.headers, ...options?.headers };
  }

  const response: T = await request(req, augments);
  return Promise.resolve(response);
}

export default checkoutApi;
